// Canonical per-country label maps and resolvers for site types and site facets.
//
// Scanner-safe: uses only relative imports and is consumed by both web (app/)
// and scanner (agencies/base/scanner/index.js via utils/availabilityRequest.js).
//
// Country keys mirror the Country enum in prisma/schema.prisma ('ca' | 'us' | 'au' | 'nz').
// app/config/countries.js re-exports SITE_TYPE_LABELS_BY_COUNTRY / SITE_FACET_LABELS_BY_COUNTRY
// for discoverability alongside other per-country UI config.

export const SITE_TYPES = [
  "rv",
  "tent",
  "rv_tent",
  "backcountry",
  "lodging",
  "group",
  "other",
];

export const SITE_FACETS = [
  "isAccessible",
  "hasElectric",
  "hasWater",
  "hasSewer",
  "isPullThrough",
  "isTentOnly",
  "isDoubleSite",
];

export const COUNTRY_CODES = ["ca", "us", "au", "nz"];

const NORTH_AMERICA_SITE_TYPES = {
  rv: "RV",
  tent: "Tent",
  rv_tent: "RV or Tent",
  backcountry: "Backcountry",
  lodging: "Lodging",
  group: "Group",
  other: "Other",
};

const NORTH_AMERICA_SITE_FACETS = {
  isAccessible: "Accessible",
  hasElectric: "Electric",
  hasWater: "Water",
  hasSewer: "Sewer",
  isPullThrough: "Pull-Through Only",
  isTentOnly: "Tents Only",
  isDoubleSite: "Double Site",
};

export const SITE_TYPE_LABELS_BY_COUNTRY = {
  ca: NORTH_AMERICA_SITE_TYPES,
  us: NORTH_AMERICA_SITE_TYPES,
  au: {
    rv: "Caravan",
    tent: "Tent",
    rv_tent: "Caravan or Tent",
    backcountry: "Hike-in",
    lodging: "Lodging",
    group: "Group Area",
    other: "Other",
  },
  nz: {
    rv: "Caravan",
    tent: "Tent",
    rv_tent: "Caravan or Tent",
    backcountry: "Tramping",
    lodging: "Lodging",
    group: "Group",
    other: "Other",
  },
};

export const SITE_FACET_LABELS_BY_COUNTRY = {
  ca: NORTH_AMERICA_SITE_FACETS,
  us: NORTH_AMERICA_SITE_FACETS,
  au: {
    ...NORTH_AMERICA_SITE_FACETS,
    hasElectric: "Powered",
  },
  nz: {
    ...NORTH_AMERICA_SITE_FACETS,
    hasElectric: "Powered",
  },
};

// Display order for site-type buttons in the request form.
// rv_tent is intentionally omitted — storage-only, explode-merged into rv + tent client-side.
const SITE_TYPE_DISPLAY_ORDER = [
  "rv",
  "tent",
  "backcountry",
  "lodging",
  "group",
  "other",
];

export function getSiteTypeLabel(type, country) {
  const labels = SITE_TYPE_LABELS_BY_COUNTRY[country];
  if (!labels) return type;
  return labels[type] ?? type;
}

export function getSiteFacetLabel(facet, country) {
  const labels = SITE_FACET_LABELS_BY_COUNTRY[country];
  if (!labels) return facet;
  return labels[facet] ?? facet;
}

export function getSortedSiteTypes(country) {
  return SITE_TYPE_DISPLAY_ORDER.map((value) => ({
    value,
    label: getSiteTypeLabel(value, country),
  }));
}
